import React, { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { usePortfolioKPILoader } from '../hooks/usePortfolioKPILoader';
import { useAuth } from './AuthContext';
import { AssignedShares, PortfolioSummary } from '../types/position';

/**
 * Positions + headers for Dive In and the Positions page (gold v2 phase 3b).
 *
 * Every header figure is a gold.portfolio_current column (the sum of the
 * rows by construction); every row figure a gold.v_position column. Nothing
 * here sums, averages or annualises (policy P5). null = unknown ("—").
 */
export interface PortfolioKPIData {
  loading: boolean;
  /** Open legs (gold.v_position is_open), each carrying its gold fields. */
  activePositions?: any[];
  /** Legs closed / assigned / expired / rolled today: they count in Today only. */
  closedTodayPositions?: any[];
  /** Headers for the whole book (lens 'all'). */
  summary: PortfolioSummary | null;
  /** Headers per lens (all | cc | csp). */
  summaryByLens?: Partial<Record<'all' | 'cc' | 'csp', PortfolioSummary>>;
  /** Shares assigned from a put with no call on them (the "still holding?" question). */
  assignedShares: AssignedShares[];
  positionCount: number;
  snapshotAsOf?: string;
}

interface PortfolioKPIContextType {
  kpis: PortfolioKPIData;
  updateKPIs: (newKPIs: PortfolioKPIData) => void;
  refreshKPIs?: () => Promise<void>;
}

export const emptyPortfolioKPIs = (loading: boolean): PortfolioKPIData => ({
  loading,
  activePositions: [],
  closedTodayPositions: [],
  summary: null,
  summaryByLens: {},
  assignedShares: [],
  positionCount: 0,
});

const PortfolioKPIContext = createContext<PortfolioKPIContextType | undefined>(undefined);

export const usePortfolioKPIs = () => {
  const context = useContext(PortfolioKPIContext);
  if (context === undefined) {
    throw new Error('usePortfolioKPIs must be used within a PortfolioKPIProvider');
  }
  return context;
};

interface PortfolioKPIProviderProps {
  children: ReactNode;
}

export const PortfolioKPIProvider: React.FC<PortfolioKPIProviderProps> = ({ children }) => {
  const [kpis, setKpis] = useState<PortfolioKPIData>(emptyPortfolioKPIs(true));
  const { loadKPIs } = usePortfolioKPILoader();
  const { isAuthenticated } = useAuth();
  const location = useLocation();
  const loadingRef = useRef(false);

  // Only the dashboard (/dive-in, /dashboard) and /portfolio/positions read this context.
  const shouldLoadPortfolioData = () => {
    const pathname = location.pathname;
    return pathname === '/dive-in' || pathname === '/dashboard' || pathname === '/portfolio/positions';
  };

  useEffect(() => {
    const initializeKPIs = async () => {
      if (loadingRef.current) return;
      loadingRef.current = true;
      try {
        setKpis(await loadKPIs());
      } catch (error) {
        console.error('Failed to initialize KPIs:', error);
        setKpis(prev => ({ ...prev, loading: false }));
      } finally {
        loadingRef.current = false;
      }
    };

    if (isAuthenticated && shouldLoadPortfolioData()) {
      initializeKPIs();
    } else {
      setKpis(prev => ({ ...prev, loading: false }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, location.pathname]);

  const updateKPIs = (newKPIs: PortfolioKPIData) => setKpis(newKPIs);

  const refreshKPIs = async () => {
    try {
      setKpis(await loadKPIs());
    } catch (error) {
      console.error('Failed to refresh KPIs:', error);
    }
  };

  return (
    <PortfolioKPIContext.Provider value={{ kpis, updateKPIs, refreshKPIs }}>
      {children}
    </PortfolioKPIContext.Provider>
  );
};
